import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Box, ImageOff, Pencil, Trash2 } from "lucide-react";
import { Toggle, confirmDelete, showErrorAlert } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import PriceTiersEditor from "./PriceTiersEditor.jsx";
import { editorPathForId } from "../../../../products/registry.js";

const editableFrom = (item) => ({
    title: item.title || "",
    note: item.note || "",
    show_price: item.show_price,
    allow_3d: item.allow_3d,
    price_tiers: (item.price_tiers || []).map((tier) => ({ ...tier })),
});

/**
 * Un modelo del proyecto en el panel (C3D-38): nombre que ve el cliente, nota,
 * precio opcional por tramos y si se puede ver en 3D. Se guarda a mano, con
 * aviso de cambios sin guardar.
 */
export default function ProjectItemEditor({ projectId, item, isFirst, isLast, onMove, onChanged }) {
    const [draft, setDraft] = useState(() => editableFrom(item));
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState(null);

    useEffect(() => { setDraft(editableFrom(item)); }, [item]);

    const dirty = JSON.stringify(draft) !== JSON.stringify(editableFrom(item));
    const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

    const save = async () => {
        setSaving(true);
        setErrors(null);
        try {
            const payload = {
                ...draft,
                price_tiers: draft.price_tiers
                    .filter((tier) => String(tier.min_quantity).trim() && String(tier.unit_price).trim())
                    .map((tier) => ({ min_quantity: Number(tier.min_quantity), unit_price: String(tier.unit_price) })),
            };
            await axios.patch(`/projects/${projectId}/items/${item.id}/`, payload);
            onChanged();
        } catch (error) {
            setErrors(error?.response?.data || { detail: "No se ha podido guardar." });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const confirmed = await confirmDelete({
            title: "Quitar del proyecto",
            text: `"${item.display_title}" deja de salir en el catálogo. El diseño no se borra.`,
            confirmButtonText: "Quitar",
        });
        if (!confirmed) return;
        try {
            await axios.delete(`/projects/${projectId}/items/${item.id}/`);
            onChanged();
        } catch {
            showErrorAlert({ text: "No se ha podido quitar el modelo." });
        }
    };

    const errorText = errors && (errors.detail || errors.price_tiers || errors.non_field_errors || JSON.stringify(errors));

    return (
        <article className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl border border-base-content/10 bg-base-100">
            <div className="flex sm:flex-col items-start gap-3 sm:w-36 shrink-0">
                <div className="w-24 sm:w-36 aspect-square rounded-lg bg-base-200 flex items-center justify-center overflow-hidden">
                    {item.image_url
                        ? <img src={getMediaUrl(item.image_url)} alt="" className="w-full h-full object-contain p-2" loading="lazy" />
                        : <ImageOff className="w-8 h-8 text-base-content/30" aria-hidden="true" />}
                </div>
                {item.kind === "design" && item.design_id && (
                    // En otra pestaña: así no se pierde lo que hay sin guardar en el proyecto.
                    <div className="flex flex-col sm:flex-row sm:w-full gap-1">
                        <Link
                            to={`/viewer/${item.design_id}`}
                            target="_blank"
                            rel="noopener"
                            className="btn btn-outline btn-xs flex-1 gap-1"
                            title="Ver el modelo en 3D (pestaña nueva)"
                        >
                            <Box className="w-3.5 h-3.5" aria-hidden="true" /> Ver 3D
                        </Link>
                        <Link
                            to={editorPathForId(item.design_id, item.product_type === "coaster" ? "keyring" : item.product_type)}
                            target="_blank"
                            rel="noopener"
                            className="btn btn-outline btn-xs flex-1 gap-1"
                            title="Abrir el diseño en el editor (pestaña nueva)"
                        >
                            <Pencil className="w-3.5 h-3.5" aria-hidden="true" /> Editar
                        </Link>
                    </div>
                )}
                <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-xs font-medium text-base-content/60">{item.product_label}</span>
                    <span className="text-xs text-base-content/60 truncate" title={item.source_name}>{item.source_name}</span>
                </div>
            </div>

            <div className="flex-1 grid gap-3 md:grid-cols-2 min-w-0">
                <div className="flex flex-col gap-3">
                    <label className="form-control">
                        <span className="label-text text-sm font-medium">Nombre en el catálogo</span>
                        <input
                            className="input input-bordered input-sm w-full"
                            value={draft.title}
                            placeholder={item.source_name}
                            onChange={(event) => set("title", event.target.value)}
                        />
                        <span className="label-text-alt text-base-content/60 mt-1">Vacío: el nombre del diseño.</span>
                    </label>
                    <label className="form-control">
                        <span className="label-text text-sm font-medium">Nota para el cliente</span>
                        <textarea
                            className="textarea textarea-bordered textarea-sm w-full"
                            rows={3}
                            value={draft.note}
                            onChange={(event) => set("note", event.target.value)}
                        />
                    </label>
                    <Toggle
                        checked={draft.allow_3d}
                        onChange={(value) => set("allow_3d", value)}
                        label={item.kind === "design" ? "Se puede ver en 3D" : "Ver en 3D (solo diseños)"}
                        disabled={item.kind !== "design"}
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <Toggle
                        checked={draft.show_price}
                        onChange={(value) => set("show_price", value)}
                        label="Enseñar precio"
                    />
                    {draft.show_price ? (
                        <PriceTiersEditor
                            tiers={draft.price_tiers}
                            onChange={(tiers) => set("price_tiers", tiers)}
                            stockPrice={item.stock_price}
                        />
                    ) : (
                        <p className="text-xs text-base-content/60">El cliente verá «Consultar precio» y tendrá que escribiros.</p>
                    )}
                </div>

                {errorText && <p role="alert" className="md:col-span-2 text-sm text-error">{String(errorText)}</p>}

                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex gap-1">
                        <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => onMove(-1)} disabled={isFirst} aria-label="Subir">
                            <ArrowUp className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => onMove(1)} disabled={isLast} aria-label="Bajar">
                            <ArrowDown className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm gap-1 text-error" onClick={remove}>
                            <Trash2 className="w-4 h-4" aria-hidden="true" /> Quitar
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        {dirty && <span className="text-xs text-warning-content bg-warning/20 rounded px-2 py-0.5">Sin guardar</span>}
                        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || saving}>
                            {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </article>
    );
}

ProjectItemEditor.propTypes = {
    projectId: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    isFirst: PropTypes.bool,
    isLast: PropTypes.bool,
    onMove: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired,
};
